/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useCallback, useContext } from "react";

// internal-deps
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import {
    CheckoutActionTypes,
    CheckoutContext,
    GroupOrderCartContext,
    GroupCartActionTypes,
} from "@swiggy-private/checkout-core";
import { GetGroupCartOrderResponse } from "@swiggy-private/cart-types";

// apis
import { getGroupCartOrderItems } from "@cart-client/apis/GroupCartOrder";
import { useEffectOnce } from "@swiggy-private/core-ui";

// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";

// helpers
import { getGroupOrderErrorPayload } from "@cart-client/helpers/GroupCartOrder";

interface useFetchGroupOrderCartReturn {
    getGroupOrderCartData: () => Promise<void>;
}

export function fetchGroupCartOrderItems(
    groupCartId: string,
): Promise<Result<GetGroupCartOrderResponse, Error | ApiResponse>> {
    return getGroupCartOrderItems(groupCartId)
        .then(groupOrderCartResponse => {
            return Ok(groupOrderCartResponse);
        })
        .catch(err => {
            return Err(err);
        });
}

const useFetchGroupOrderCart = (
    groupCartId: string,
    enablePolling: (resumeWithDelay?: boolean) => void,
    disablePolling: VoidFunction,
): useFetchGroupOrderCartReturn => {
    const { state, setState, setIsGroupCartFlow } = useContext(GroupOrderCartContext);
    const { checkoutState } = useContext(CheckoutContext);

    useEffectOnce(() => {
        setState({ type: GroupCartActionTypes.GROUP_CART_STATE_FETCH_IN_PROGRESS });
        setIsGroupCartFlow(true);

        // enable polling in case of regular shared cart flow and not in case of unlock flow
        if (checkoutState.type === CheckoutActionTypes.SHARED_CART) {
            enablePolling();
        }
        return (): void => {
            disablePolling();
        };
    });

    const getGroupOrderCartData = useCallback(async () => {
        try {
            setState({ type: GroupCartActionTypes.GROUP_CART_STATE_FETCH_IN_PROGRESS });

            // Pause polling
            disablePolling();

            const data = await fetchGroupCartOrderItems(groupCartId);

            if (data.isErr()) {
                throw data;
            }
            const groupCartOrderData = data.unwrap();
            const errorPayload = getGroupOrderErrorPayload(groupCartOrderData);

            setState({
                type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                payload: {
                    data: groupCartOrderData.data,
                    error: errorPayload,
                },
            });

            // re-enable back polling with a delay of 3s, since latest data was already synced with manual refresh
            enablePolling(true);
        } catch (err) {
            setState({
                type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                payload: {
                    error: { ...state?.error, error: true },
                },
            });

            // re-enable back polling with a delay of 3s, since latest data was already synced with manual refresh
            enablePolling(true);
        }
    }, [setState, state, disablePolling, groupCartId, enablePolling]);

    return {
        getGroupOrderCartData,
    };
};

export default useFetchGroupOrderCart;
